import { FormEvent, KeyboardEvent, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router";
import GridShape from "../../components/common/GridShape";
import Button from "../../components/ui/button/Button";
import PageMeta from "../../components/common/PageMeta";
import RegisterLoader from "../../components/common/RegisterLoader";
import { verifyTwoFactorLogin } from "../../services/authService";
import { useLanguage } from "../../context/LanguageContext";
import { getLocalizedFallback, localizeApiMessage } from "../../utils/apiMessageI18n";
import { useAuthUser } from "../../context/AuthUserContext";

export default function TwoFactorVerify() {
  const { language } = useLanguage();
  const { refreshUser } = useAuthUser();
  const navigate = useNavigate();
  const [digits, setDigits] = useState<string[]>(() => Array(6).fill(""));
  const digitRefs = useRef<Array<HTMLInputElement | null>>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const pageTitle = getLocalizedFallback(language, {
    pt: "Verificação 2FA",
    es: "Verificación 2FA",
    en: "2FA Verification",
  });
  const pageDescription = getLocalizedFallback(language, {
    pt: "Tela para verificação de dois fatores.",
    es: "Pantalla de verificación de dos factores.",
    en: "Two-factor verification screen.",
  });
  const headingText = getLocalizedFallback(language, {
    pt: "Verificação em dois fatores",
    es: "Verificación en dos factores",
    en: "Two-Factor Verification",
  });
  const subtitleText = getLocalizedFallback(language, {
    pt: "Digite o código de 6 dígitos do seu aplicativo autenticador.",
    es: "Ingresa el código de 6 dígitos de tu aplicación autenticadora.",
    en: "Enter the 6-digit code from your authenticator app.",
  });
  const submitButtonText = getLocalizedFallback(language, {
    pt: "Validar e continuar",
    es: "Validar y continuar",
    en: "Verify and continue",
  });
  const backToSignInText = getLocalizedFallback(language, {
    pt: "Voltar para entrar",
    es: "Volver a iniciar sesión",
    en: "Back to sign in",
  });

  useEffect(() => {
    const mfaToken = localStorage.getItem("mfa_access_token");
    if (!mfaToken) {
      navigate("/signin", { replace: true });
    }
  }, [navigate]);

  const canSubmit = digits.every((digit) => digit.length === 1) && !isSubmitting;

  const handleSubmit = async (
    event?: FormEvent<HTMLFormElement>,
    codeOverride?: string
  ) => {
    event?.preventDefault();
    const code = (codeOverride ?? digits.join("")).trim();
    if (isSubmitting || code.length < 6) return;

    const mfaToken = localStorage.getItem("mfa_access_token");
    if (!mfaToken) {
      navigate("/signin", { replace: true });
      return;
    }

    setIsSubmitting(true);
    setErrorMessage("");
    try {
      const response = await verifyTwoFactorLogin({ code, mfaToken });
      const accessToken = response.data?.access_token;
      if (!accessToken) {
        throw new Error(
          getLocalizedFallback(language, {
            pt: "Nao foi possivel concluir a verificacao 2FA.",
            es: "No fue posible completar la verificacion 2FA.",
            en: "Could not complete 2FA verification.",
          })
        );
      }

      localStorage.removeItem("mfa_access_token");
      localStorage.removeItem("mfa_expires_in");
      localStorage.setItem("access_token", accessToken);
      localStorage.setItem("token_type", response.data?.token_type || "Bearer");
      localStorage.setItem("expires_in", String(response.data?.expires_in ?? ""));
      await refreshUser();
      navigate("/");
    } catch (error) {
      const fallback = getLocalizedFallback(language, {
        pt: "Codigo 2FA invalido.",
        es: "Codigo 2FA invalido.",
        en: "Invalid 2FA code.",
      });
      const rawMessage = error instanceof Error ? error.message : "";
      const localizedMessage = localizeApiMessage(rawMessage, language) || fallback;
      setErrorMessage(localizedMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDigitChange = (index: number, value: string) => {
    const digit = value.replace(/\D/g, "").slice(-1);
    const nextDigits = [...digits];
    nextDigits[index] = digit;
    setDigits(nextDigits);

    if (digit && index < 5) {
      digitRefs.current[index + 1]?.focus();
    }

    if (nextDigits.every((item) => item.length === 1)) {
      void handleSubmit(undefined, nextDigits.join(""));
    }
  };

  const handleDigitKeyDown = (index: number, event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Backspace" && !digits[index] && index > 0) {
      digitRefs.current[index - 1]?.focus();
    }
  };

  const handlePasteCode = (value: string) => {
    const pastedDigits = value.replace(/\D/g, "").slice(0, 6).split("");
    if (!pastedDigits.length) return;

    const filled = [...Array(6)].map((_, idx) => pastedDigits[idx] || "");
    setDigits(filled);
    const focusIndex = Math.min(pastedDigits.length, 6) - 1;
    if (focusIndex >= 0) {
      digitRefs.current[focusIndex]?.focus();
    }
    if (filled.every((item) => item.length === 1)) {
      void handleSubmit(undefined, filled.join(""));
    }
  };

  return (
    <>
      <PageMeta
        title={`${pageTitle} | Gerify`}
        description={pageDescription}
      />
      {isSubmitting && <RegisterLoader />}
      <div className="relative flex w-full h-screen px-4 py-6 overflow-hidden bg-white z-1 dark:bg-gray-900 sm:p-0">
        <div className="flex flex-col flex-1 p-6 rounded-2xl sm:rounded-none sm:border-0 sm:p-8">
          <div className="flex flex-col justify-center flex-1 w-full max-w-md mx-auto">
            <div className="mb-5 text-center sm:mb-8">
              <h1 className="mb-2 font-semibold text-gray-800 text-title-sm dark:text-white/90 sm:text-title-md">
                {headingText}
              </h1>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {subtitleText}
              </p>
            </div>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div
                className="flex items-center justify-center gap-2"
                onPaste={(event) => {
                  event.preventDefault();
                  handlePasteCode(event.clipboardData.getData("text"));
                }}
              >
                {[0, 1, 2, 3, 4, 5].map((index) => (
                  <div key={index} className="flex items-center">
                    {index === 3 && (
                      <span className="mx-2 text-sm font-semibold text-gray-500 dark:text-gray-400">
                        -
                      </span>
                    )}
                    <input
                      ref={(el) => {
                        digitRefs.current[index] = el;
                      }}
                      type="text"
                      inputMode="numeric"
                      maxLength={1}
                      value={digits[index]}
                      onChange={(e) => handleDigitChange(index, e.target.value)}
                      onKeyDown={(e) => handleDigitKeyDown(index, e)}
                      className="h-11 w-11 rounded-lg border border-gray-300 bg-transparent text-center text-base font-semibold text-gray-800 shadow-theme-xs focus:border-brand-300 focus:outline-none focus:ring focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90"
                    />
                  </div>
                ))}
              </div>
              {errorMessage && (
                <p className="text-sm text-error-500" role="alert">
                  {errorMessage}
                </p>
              )}
              <div>
                <Button className="w-full" size="sm" disabled={!canSubmit}>
                  {submitButtonText}
                </Button>
              </div>
              <div>
                <Link
                  to="/signin"
                  className="text-sm text-brand-500 hover:text-brand-600 dark:text-brand-400"
                  onClick={() => {
                    localStorage.removeItem("mfa_access_token");
                    localStorage.removeItem("mfa_expires_in");
                  }}
                >
                  {backToSignInText}
                </Link>
              </div>
            </form>
          </div>
        </div>
        <div className="relative items-center justify-center flex-1 hidden p-8 z-1 bg-brand-950 dark:bg-white/5 lg:flex">
          <GridShape />
          <div className="flex flex-col items-center max-w-lg">
            <div className="block mb-4">
              <img
                src="./images/logo/auth-logo.svg"
                alt="Logo"
                className="w-[520px] max-w-full"
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
